import { useNode } from "@craftjs/core";
import React from "react";
import {
  ColorInput,
  RangeInput,
  SelectInput,
  UnitInput,
  PaddingInput,
  MarginInput,
  Panel,
  Tabs,
  Spacer,
  AlignInput,
  ToggleInput,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { isUpdatedFromDefault } from "../../utils";
import { INPUT_LIMITS } from "../../input-limits";

export const SiteTitleSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    align,
    alignTablet,
    alignMobile,
    structureTag = "h1",
    layoutColumns,
    layoutColumnsGap,
    siteTitle,
    textColor,
    backgroundColor,
    typoSize,
    typoSizeTablet,
    typoSizeMobile,
    typoLineHeight,
    typoLetterSpacing,
    typoWordSpacing,
    typoTransform,
    typoStyle,
    typoDecoration,
    typoWeight,
    layoutPadding,
    layoutPaddingTablet,
    layoutPaddingMobile,
    layoutMargin,
    layoutMarginTablet,
    layoutMarginMobile,
    linkSite,
    openInNewTab,
    link = "#",
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <SelectInput
            resetValue={"h1"}
            value={structureTag}
            onChange={(val) => {
              setProp((props) => {
                props.structureTag = val;
              });
            }}
            options={[
              {
                label: "Heading 1",
                value: "h1",
              },
              {
                label: "Heading 2",
                value: "h2",
              },
              {
                label: "Heading 3",
                value: "h3",
              },
              {
                label: "Heading 4",
                value: "h4",
              },
              {
                label: "Heading 5",
                value: "h5",
              },
              {
                label: "Heading 6",
                value: "h6",
              },
            ]}
            label={"Tag"}
          />

          <AlignInput
            isResponsive={true}
            resetValue={undefined}
            value={align}
            valueTablet={alignTablet}
            valueMobile={alignMobile}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.alignTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.alignMobile = val;
              });
            }}
            label={"Align"}
          />
          <ToggleInput
            isResetVisible={false}
            label="Link to Home"
            value={linkSite}
            onChange={(val) => {
              setProp((props) => {
                props.linkSite = val;
              });
            }}
          />
          {linkSite && (
            <>
              <ToggleInput
                resetValue={undefined}
                label="Open in new tab"
                value={openInNewTab}
                onChange={(val) => {
                  setProp((props) => {
                    props.openInNewTab = val;
                  });
                }}
              />
            </>
          )}
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: typoSize },
            { key: typoSizeTablet },
            { key: typoSizeMobile },
            { key: typoLineHeight },
            { key: typoLetterSpacing },
            { key: typoWordSpacing },
            { key: typoWeight },
            { key: typoTransform },
            { key: typoDecoration },
            { key: typoStyle },
          ])}
          id={`block-setting-${id}-typography`}
          title={"Typography"}
          initialOpen={false}
        >
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            value={typoSize}
            valueTablet={typoSizeTablet}
            valueMobile={typoSizeMobile}
            onChange={(val) => {
              setProp((props) => {
                props.typoSize = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.typoSizeTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.typoSizeMobile = val;
              });
            }}
            label={"Size"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoLineHeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoLineHeight = val;
              });
            }}
            label={"Line Height"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoLetterSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoLetterSpacing = val;
              });
            }}
            label={"Letter Spacing"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoWordSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoWordSpacing = val;
              });
            }}
            label={"Word Spacing"}
          />
          <SelectInput
            resetValue={undefined}
            value={typoWeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoWeight = val;
              });
            }}
            options={[
              { label: "100", value: "100" },
              { label: "200", value: "200" },
              { label: "300", value: "300" },
              { label: "400", value: "400" },
              { label: "500", value: "500" },
              { label: "600", value: "600" },
              { label: "700", value: "700" },
              { label: "800", value: "800" },
              { label: "900", value: "900" },
            ]}
            label="Weight"
          />
          <SelectInput
            resetValue={undefined}
            value={typoTransform}
            onChange={(val) => {
              setProp((props) => {
                props.typoTransform = val;
              });
            }}
            options={[
              { label: "Uppercase", value: "uppercase" },
              { label: "Lowercase", value: "lowercase" },
              { label: "Capitalize", value: "capitalize" },
              { label: "None", value: "none" },
            ]}
            label="Transform"
          />
          <SelectInput
            resetValue={undefined}
            value={typoDecoration}
            onChange={(val) => {
              setProp((props) => {
                props.typoDecoration = val;
              });
            }}
            options={[
              { label: "Underline", value: "underline" },
              { label: "Overline", value: "overline" },
              { label: "None", value: "none" },
            ]}
            label="Decoration"
          />
          <SelectInput
            resetValue={undefined}
            value={typoStyle}
            onChange={(val) => {
              setProp((props) => {
                props.typoStyle = val;
              });
            }}
            options={[
              {
                label: "Normal",
                value: "normal",
              },
              {
                label: "Italic",
                value: "italic",
              },
            ]}
            label="Style"
          />
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: textColor },
            { key: backgroundColor },
          ])}
          id={`block-setting-${id}-colors`}
          title={"Colors"}
          initialOpen={true}
        >
          <ColorInput
            resetValue={undefined}
            value={textColor}
            onChange={(val) => {
              setProp((props) => {
                props.textColor = val;
              });
            }}
            label={"Text"}
          />
          <ColorInput
            resetValue={undefined}
            value={backgroundColor}
            onChange={(val) => {
              setProp((props) => {
                props.backgroundColor = val;
              });
            }}
            label={"Background"}
          />
        </Panel>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutMarginTablet },
            { key: layoutMarginMobile },
            { key: layoutMargin },
            { key: layoutPaddingTablet },
            { key: layoutPaddingMobile },
            { key: layoutPadding },
          ])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={true}
        >
          <MarginInput
            isResponsive={true}
            valueTablet={layoutMarginTablet}
            valueMobile={layoutMarginMobile}
            resetValue={undefined}
            value={layoutMargin}
            onChange={(val) => {
              setProp((props) => {
                props.layoutMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutMarginMobile = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            isResponsive={true}
            valueTablet={layoutPaddingTablet}
            valueMobile={layoutPaddingMobile}
            resetValue={undefined}
            value={layoutPadding}
            onChange={(val) => {
              setProp((props) => {
                props.layoutPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutPaddingMobile = val;
              });
            }}
            label={"Padding"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutColumns },
            { key: layoutColumnsGap },
          ])}
          id={`block-setting-${id}-columns`}
          title={"Columns"}
          initialOpen={false}
        >
          <RangeInput
            resetValue={undefined}
            value={layoutColumns}
            onChange={(val) => {
              setProp((props) => {
                props.layoutColumns = val;
              });
            }}
            // min={1}
            // max={10}
            label={"Columns"}
            min={INPUT_LIMITS["site-title-columns"].min}
            max={INPUT_LIMITS["site-title-columns"].max}
            step={INPUT_LIMITS["site-title-columns"].step}
          />
          <UnitInput
            resetValue={undefined}
            value={layoutColumnsGap}
            onChange={(val) => {
              setProp((props) => {
                props.layoutColumnsGap = val;
              });
            }}
            label={"Columns Gap"}
          />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: structureTag },
        { key: alignTablet },
        { key: alignMobile },
        { key: align },
        { key: linkSite },
        { key: openInNewTab },
        { key: typoSize },
        { key: typoSizeTablet },
        { key: typoSizeMobile },
        { key: typoLineHeight },
        { key: typoLetterSpacing },
        { key: typoWordSpacing },
        { key: typoWeight },
        { key: typoTransform },
        { key: typoDecoration },
        { key: typoStyle },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: textColor },
        { key: backgroundColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutMarginTablet },
        { key: layoutMarginMobile },
        { key: layoutMargin },
        { key: layoutPaddingTablet },
        { key: layoutPaddingMobile },
        { key: layoutPadding },
        { key: layoutColumns },
        { key: layoutColumnsGap },
      ]),
    },
  ];

  return (
    <div>
      <Tabs items={tabs} id={`block-settings-tab-${id}`} />
    </div>
  );
};
